import urlContext, { Contexts } from './urlContext/urlContext';

/**
 * Firebase Web App configuration.
 *
 * NOTE ON SECRETS: the values below (apiKey, appId, messagingSenderId, …) are
 * the Firebase *Web* client identifiers. They are NOT secrets — they ship in
 * every Firebase web bundle and are safe to commit (access is governed by
 * Firebase Auth + backend token verification, not by hiding these ids). The
 * OIDC client secret for id.utah.gov is configured ONLY in the Firebase
 * console (Auth provider config) and is never present in this bundle.
 *
 * Each value may be overridden at build time via a `process.env.*` variable
 * (injected by webpack `DefinePlugin` / available under jest). When no override
 * is supplied we fall back to the per-context public config selected by
 * `urlContext()` (hostname based — same mechanism the webservice URL uses).
 */

// Build-time overrides. These MUST use STATIC `process.env.X` member access so
// webpack's DefinePlugin can inline them into the browser bundle — DefinePlugin
// cannot replace dynamic `process.env[key]` access. DefinePlugin (see
// webpack.common.js) defines each key as a string literal (empty when unset);
// under jest, `process.env` is read directly. An empty/unset value falls through
// to the per-context `baseConfig` below.
const OVERRIDES = {
  FIREBASE_API_KEY: process.env.FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN: process.env.FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID: process.env.FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET: process.env.FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID: process.env.FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID: process.env.FIREBASE_APP_ID,
  FIREBASE_OIDC_PROVIDER_ID: process.env.FIREBASE_OIDC_PROVIDER_ID,
  FIREBASE_OIDC_LOGOUT_URL: process.env.FIREBASE_OIDC_LOGOUT_URL,
  FIREBASE_OIDC_POST_LOGOUT_REDIRECT_URI:
    process.env.FIREBASE_OIDC_POST_LOGOUT_REDIRECT_URI,
};
const env = (key) => OVERRIDES[key] || undefined;

// Per-context public Firebase web config.
//
// authDomain and the OIDC "Callback URL" (redirect_uri) are LINKED:
//  - Firebase's browser-side redirect helper is served at
//    `https://<authDomain>/__/auth/handler`, and THAT exact URL is the
//    `redirect_uri` Firebase sends to the id.utah.gov (ForgeRock AM) /authorize
//    endpoint. So the redirect_uri FOLLOWS authDomain: with a connected custom
//    domain it is `https://<custom-domain>/__/auth/handler`, NOT the
//    `<projectId>.firebaseapp.com` handler. EVERY authDomain the app can run
//    under MUST be registered as a Callback URL on the AM OAuth2 client, or AM
//    returns HTTP 400 (`redirect_uri_mismatch`) from /authorize and login never
//    starts.
//  - `authDomain` MUST be FIRST-PARTY to the app origin, otherwise the browser
//    partitions the redirect state as third-party and getRedirectResult comes
//    back empty (login silently never completes). So it is the app's own custom
//    domain, which must serve the reserved /__/auth/* routes (Firebase Hosting
//    connected custom domain; the SPA "**" rewrite does NOT shadow them).
const CONTEXT_CONFIG = {
  [Contexts.PROD]: {
    apiKey: 'AIzaSyCewa1BC1mNicVdfET-jnP3s0LRjXmKopE',
    // First-party app domain (serves /__/auth/*). Because authDomain drives the
    // OIDC redirect_uri, each connected PROD domain's handler must be a Callback
    // URL on the PROD AM client: https://pharma.utah.gov/__/auth/handler and
    // (v2) https://pharma2.utah.gov/__/auth/handler.
    authDomain: 'pharma.utah.gov',
    projectId: 'ut-uid-pharma-prod',
    storageBucket: 'ut-uid-pharma-prod.appspot.com',
    messagingSenderId: '813691296680',
    appId: '1:813691296680:web:1c27d17e05c649b6e21b9d',
    measurementId: "G-V6GSH2CEC6",
  },
  [Contexts.AT]: {
    apiKey: 'AIzaSyCKpr2cbX-6HMMG8GSWXlsmbG9uM5JBaKI',
    // First-party app domain (serves /__/auth/*). Because authDomain drives the
    // OIDC redirect_uri, each connected AT domain's handler must be a Callback
    // URL on the AT AM client: https://pharma.at.utah.gov/__/auth/handler and
    // (v2) https://pharma2.at.utah.gov/__/auth/handler. Do NOT set authDomain to
    // the firebaseapp.com domain: that makes the redirect state third-party to
    // the app origin and getRedirectResult returns empty (sign-in never
    // completes).
    authDomain: 'pharma.at.utah.gov',
    projectId: 'ut-uid-pharma-at',
    storageBucket: 'ut-uid-pharma-at.appspot.com',
    messagingSenderId: '779991300570',
    appId: '1:779991300570:web:30860cf859f67db90a28e3',
    measurementId: "G-VJR8P04C31"
  },
};
// dev/local reuse the AT project (closest to a non-prod backend).
CONTEXT_CONFIG[Contexts.DEV] = CONTEXT_CONFIG[Contexts.AT];
CONTEXT_CONFIG[Contexts.LOCAL] = CONTEXT_CONFIG[Contexts.AT];

const baseConfig =
  CONTEXT_CONFIG[urlContext()] || CONTEXT_CONFIG[Contexts.PROD];

// Firebase Auth's browser-side redirect state must live on a domain that is
// FIRST-PARTY to the app origin, or getRedirectResult() returns empty
// (third-party partitioning) and sign-in silently never completes. Every
// connected custom domain of a Firebase Hosting site serves the reserved
// /__/auth/* routes, so when the app is served from one of its known custom
// domains, use THAT host as the authDomain — including the parallel v2 sites,
// which share the same per-context project config but are served from a
// different first-party domain:
//   v1: pharma.at.utah.gov (AT) / pharma.utah.gov (PROD)
//   v2: pharma2.at.utah.gov (AT) / pharma2.utah.gov (PROD)
// Otherwise (e.g. *.web.app default domains, local) fall back to the per-context
// default authDomain.
const FIRST_PARTY_AUTH_HOSTS = new Set([
  'pharma.at.utah.gov',
  'pharma2.at.utah.gov',
  'pharma.utah.gov',
  'pharma2.utah.gov',
]);
const runtimeAuthHost =
  typeof document === 'undefined' ? '' : document.location.hostname;
const firstPartyAuthDomain = FIRST_PARTY_AUTH_HOSTS.has(runtimeAuthHost)
  ? runtimeAuthHost
  : undefined;

export const firebaseConfig = {
  apiKey: env('FIREBASE_API_KEY') || baseConfig.apiKey,
  authDomain:
    env('FIREBASE_AUTH_DOMAIN') || firstPartyAuthDomain || baseConfig.authDomain,
  projectId: env('FIREBASE_PROJECT_ID') || baseConfig.projectId,
  storageBucket: env('FIREBASE_STORAGE_BUCKET') || baseConfig.storageBucket,
  messagingSenderId:
    env('FIREBASE_MESSAGING_SENDER_ID') || baseConfig.messagingSenderId,
  appId: env('FIREBASE_APP_ID') || baseConfig.appId,
  measurementId: env('FIREBASE_MEASUREMENT_ID') || baseConfig.measurementId,
};

/**
 * The Firebase Auth provider id for the id.utah.gov custom OIDC provider.
 * MUST exactly match the provider id created in the Firebase console and MUST
 * begin with `oidc.` (Firebase requirement). Both the AT and prod projects
 * define the provider as `pharma-idp` (provider id `oidc.pharma-idp`).
 * Configurable via `FIREBASE_OIDC_PROVIDER_ID`.
 */
export const utahIdProviderId =
  env('FIREBASE_OIDC_PROVIDER_ID') || 'oidc.pharma-idp';

/**
 * The Utah ID (id.utah.gov / ForgeRock AM) OIDC **RP-initiated logout** endpoint
 * (`end_session_endpoint` from the IdP's OIDC discovery document). Redirecting
 * here with a valid `id_token_hint` ends the AM SSO session; when a
 * `post_logout_redirect_uri` that is REGISTERED on the AM OAuth2 client is also
 * supplied, AM returns the browser to it. Override with `FIREBASE_OIDC_LOGOUT_URL`.
 *
 * NOTE: the bare `mylogin.utah.gov/logout?goto=` host is NOT the IdP logout
 * endpoint and does not honor `goto` — the real endpoint is AM's
 * `<issuer>/connect/endSession` below (per the discovery `end_session_endpoint`).
 */
export const oidcLogoutUrl =
  env('FIREBASE_OIDC_LOGOUT_URL') ||
  'https://sso.mylogin.utah.gov/am/oauth2/connect/endSession';

/**
 * The post-logout return URL sent to AM's `endSession` as `post_logout_redirect_uri`.
 * AM only redirects back to a value that is EXACTLY REGISTERED on the OAuth2
 * client (an IdP-side/DTS task); an unregistered value is rejected with
 * `redirect_uri_mismatch`. So this is OPT-IN: set `FIREBASE_OIDC_POST_LOGOUT_REDIRECT_URI`
 * to the registered return URL to enable the redirect-back. When unset, sign-out
 * signs the app out locally and lands on its own signed-out page (it does not
 * strand the user on AM's logged-out page).
 */
export const oidcPostLogoutRedirectUri =
  env('FIREBASE_OIDC_POST_LOGOUT_REDIRECT_URI') || undefined;

export default firebaseConfig;
